/**
 * Shop listings: filter drawer, in-place results, load more, Quick Add sizes,
 * colour swatches and grid density. Port of the redesign's collection page,
 * working on server-rendered WooCommerce markup. Every action here is also a
 * plain link or form, so the page works without this script.
 */
const SWAP = ['.chips-row', '#plp-toolbar', '#plp-results', '#filters'];
const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
let controller = null;

const $ = (s, root = document) => root.querySelector(s);
const $$ = (s, root = document) => [...root.querySelectorAll(s)];

// ---------------------------------------------------------------------------
// URL building
// ---------------------------------------------------------------------------
function formToUrl(form) {
  const url = new URL(form.action, location.origin);
  const params = new Map();
  for (const [rawKey, value] of new FormData(form)) {
    if (value === '') continue;
    const key = rawKey.replace(/\[\]$/, '');
    if (key === 'tc_price') {
      const [min, max] = String(value).split('-');
      if (min !== '') params.set('min_price', [min]);
      if (max) params.set('max_price', [max]);
      continue;
    }
    params.set(key, [...(params.get(key) || []), value]);
  }
  params.forEach((vals, key) => url.searchParams.set(key, vals.join(',')));
  return url.toString();
}

// ---------------------------------------------------------------------------
// Fetch and swap
// ---------------------------------------------------------------------------
async function navigate(href, { push = true, scroll = true } = {}) {
  const results = $('#plp-results');
  if (!results) { location.href = href; return; }
  controller?.abort();
  controller = new AbortController();
  results.setAttribute('aria-busy', 'true');
  try {
    const res = await fetch(href, { credentials: 'same-origin', signal: controller.signal, headers: { 'X-TC-Partial': 'plp' } });
    if (!res.ok || res.redirected && new URL(res.url).pathname !== new URL(href, location.origin).pathname) {
      location.href = res.url || href;
      return;
    }
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const dlg = $('#filters');
    if (dlg?.open) dlg.close();
    SWAP.forEach((sel) => {
      const next = $(sel, doc);
      const cur = $(sel);
      if (cur && next) cur.replaceWith(next);
      else if (cur && !next) cur.remove();
      else if (!cur && next && sel === '.chips-row') $('#plp-toolbar')?.before(next);
    });
    document.title = doc.title;
    if (push) history.pushState({ plp: true }, '', res.url || href);
    init();
    if (scroll) {
      const top = $('.chips-row') || $('#plp-toolbar');
      if (top && top.getBoundingClientRect().top < 0) top.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth' });
    }
    const count = $('#plp-toolbar .count')?.textContent.trim();
    if (count) announce(count);
  } catch (e) {
    if (e.name !== 'AbortError') location.href = href;
  } finally {
    $('#plp-results')?.setAttribute('aria-busy', 'false');
  }
}

function announce(text) {
  let live = $('#plp-live');
  if (!live) {
    live = document.createElement('p');
    live.id = 'plp-live';
    live.className = 'screen-reader-text';
    live.setAttribute('aria-live', 'polite');
    document.body.append(live);
  }
  live.textContent = text;
}

async function loadMore(btn) {
  const grid = $('[data-grid]');
  if (!grid) return;
  btn.setAttribute('aria-busy', 'true');
  btn.textContent = btn.textContent.replace(/…$/, '') + '…';
  try {
    const res = await fetch(btn.href, { credentials: 'same-origin' });
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const cards = $$('[data-grid] > li', doc);
    const firstNew = cards[0];
    grid.append(...cards);
    const nextMore = $('[data-more]', doc);
    const curMore = $('[data-more]');
    if (curMore) nextMore ? curMore.replaceWith(nextMore) : curMore.remove();
    initMore();
    firstNew?.querySelector('a[data-card-link]:not([tabindex])')?.focus({ preventScroll: true });
  } catch (e) {
    location.href = btn.href;
  }
}

// ---------------------------------------------------------------------------
// Pieces that need (re)binding after a swap
// ---------------------------------------------------------------------------
function initMore() {
  $$('[data-more-next]').forEach((b) => { b.hidden = false; });
}

function initDensity() {
  const dens = $('.dens');
  const grid = $('[data-grid]');
  if (!dens || !grid) return;
  dens.hidden = false;
  let cols = '4';
  try { cols = localStorage.getItem('tc-cols') || '4'; } catch (e) { /* storage off */ }
  const apply = (c) => {
    grid.dataset.cols = c;
    $$('button', dens).forEach((b) => {
      b.classList.toggle('on', b.dataset.cols === c);
      b.setAttribute('aria-pressed', String(b.dataset.cols === c));
    });
  };
  apply(cols);
  dens.onclick = (e) => {
    const b = e.target.closest('button[data-cols]');
    if (!b) return;
    apply(b.dataset.cols);
    try { localStorage.setItem('tc-cols', b.dataset.cols); } catch (err) { /* storage off */ }
  };
}

function init() {
  initMore();
  initDensity();
}

// ---------------------------------------------------------------------------
// Delegated events (bound once)
// ---------------------------------------------------------------------------
function closeSizePanels(except) {
  $$('.qa-sizes:not([hidden])').forEach((panel) => {
    if (panel === except) return;
    panel.hidden = true;
    panel.closest('.card')?.querySelector('[data-qa-open]')?.setAttribute('aria-expanded', 'false');
  });
}

document.addEventListener('click', (e) => {
  const t = e.target;

  // Filter drawer.
  if (t.closest('[data-fd-open]')) {
    const dlg = $('#filters');
    if (dlg?.showModal) { e.preventDefault(); dlg.showModal(); }
    return;
  }
  if (t.closest('[data-fd-close]') || (t.id === 'filters' && t.tagName === 'DIALOG')) {
    $('#filters')?.close();
    return;
  }

  // Quick Add: reveal size buttons.
  const opener = t.closest('[data-qa-open]');
  if (opener) {
    e.preventDefault();
    const panel = document.getElementById(opener.getAttribute('aria-controls'));
    if (!panel) return;
    const show = panel.hidden;
    closeSizePanels(panel);
    panel.hidden = !show;
    opener.setAttribute('aria-expanded', String(show));
    if (show) panel.querySelector('a')?.focus();
    return;
  }
  if (!t.closest('.qa-sizes')) closeSizePanels();

  // Load more.
  const more = t.closest('[data-more-next]');
  if (more) { e.preventDefault(); loadMore(more); return; }

  // In-place filter links (chips, active filters, clear all).
  const link = t.closest('a[data-plp-link]');
  if (link && !e.metaKey && !e.ctrlKey && !e.shiftKey && link.origin === location.origin) {
    e.preventDefault();
    navigate(link.href);
  }
});

document.addEventListener('submit', (e) => {
  const form = e.target;
  if (form.matches('[data-fd-form]')) {
    e.preventDefault();
    navigate(formToUrl(form));
  } else if (form.matches('.woocommerce-ordering')) {
    e.preventDefault();
    const url = new URL(location.href);
    url.searchParams.set('orderby', form.querySelector('select').value);
    url.pathname = url.pathname.replace(/\/page\/\d+\/?$/, '/');
    navigate(url.toString(), { scroll: false });
  }
});

// WooCommerce's own script submits the sort form on change via jQuery; catch the change first.
document.addEventListener('change', (e) => {
  if (e.target.matches('.woocommerce-ordering select.orderby')) {
    e.stopImmediatePropagation();
    e.target.form.requestSubmit();
  }
}, true);

// Colour swatches: preview the colour on the card (hover/focus). Tap/click follows the link.
function previewSwatch(a) {
  const card = a.closest('.card');
  if (!card || !a.dataset.img) return;
  const i1 = card.querySelector('.card__img .i1');
  const i2 = card.querySelector('.card__img .i2');
  if (i1) { i1.removeAttribute('srcset'); i1.src = a.dataset.img; i1.classList.toggle('has2', !!a.dataset.img2); }
  if (i2 && a.dataset.img2) { i2.removeAttribute('srcset'); i2.src = a.dataset.img2; }
  card.querySelectorAll('[data-card-link]').forEach((l) => { l.href = a.href; });
  card.querySelectorAll('.vdots a').forEach((s) => {
    const on = s === a;
    s.classList.toggle('on', on);
    on ? s.setAttribute('aria-current', 'true') : s.removeAttribute('aria-current');
  });
}
if (matchMedia('(hover: hover)').matches) {
  document.addEventListener('pointerover', (e) => {
    const a = e.target.closest('.vdots a');
    if (a) previewSwatch(a);
  });
}
document.addEventListener('focusin', (e) => {
  const a = e.target.closest('.vdots a');
  if (a) previewSwatch(a);
});

document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeSizePanels(); });
document.addEventListener('tc:cart:added', () => closeSizePanels());

window.addEventListener('popstate', () => {
  if ($('#plp-results')) navigate(location.href, { push: false, scroll: false });
});

init();
